import map from "lodash/map";
import omit from "lodash/omit";
import {
    createContext,
    ReactElement,
    ReactNode,
    Suspense,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { useAuth } from "react-oidc-context";
import { matchPath, useLocation } from "react-router-dom";
import { mappedRoutes } from "./Router";
import { ErrorBoundary } from "@/frontend/@error/ErrorBoundary";
import { useLoggedInUser } from "@/frontend/user/LoggedInUserProvider";

export interface PreloadElement {
    key: string;
    element: ReactElement;
}

interface PreloadContextType {
    setPreloadRoute: (path: string) => void;
    setPreloadElement: (preloadElement: PreloadElement) => void;
    isProviderActive: boolean;
    isPreloading: boolean;
}

interface PreloadState {
    element: ReactElement;
}

const CLEAR_TIMER = 3_000;
const RECENCY_TIMER = 20_000;

const PreloadContext = createContext<PreloadContextType>({
    setPreloadRoute: () => undefined,
    setPreloadElement: () => undefined,
    isProviderActive: false,
    isPreloading: false
});

export const usePreload = () => {
    return useContext(PreloadContext);
};

export const PreloadProvider = ({ children }: { children: ReactNode }) => {
    const { isSuper } = useLoggedInUser();
    const [state, setState] = useState<Record<string, PreloadState>>({});

    const historyRef = useRef<Record<string, number>>({});
    const checkAndSetRecency = (key: string) => {
        if (historyRef.current[key]) {
            const threshold = Date.now() - RECENCY_TIMER;
            if (historyRef.current[key] > threshold) {
                // preload already happened recently
                return false;
            }
        }
        historyRef.current[key] = Date.now();
        return true;
    };

    const removeKey = (key: string) => {
        setState(prevState => omit(prevState, key));
    };

    const { pathname } = useLocation();
    useEffect(() => {
        // clearing preload after page change
        removeKey(`route:${pathname}`);
    }, [pathname]);

    const setPreloadRoute = (path: string) => {
        const key = `route:${path}`;
        if (!checkAndSetRecency(`route:${path}`)) {
            return;
        }

        // Preload the path
        mappedRoutes.forEach(route => {
            if (route.allowAnyone || isSuper) {
                const match = matchPath(
                    {
                        path: route.path
                    },
                    path
                );
                if (match) {
                    // preloading path
                    setState(x => ({
                        ...x,
                        [key]: {
                            element: route.render(
                                match.params as never,
                                undefined
                            )
                        }
                    }));
                }
            }
        });
    };

    const setPreloadElement = (value: PreloadElement) => {
        const key = `element:${value.key}`;
        if (!checkAndSetRecency(key)) {
            return;
        }

        setState(x => ({
            ...x,
            [key]: { element: value.element }
        }));
    };

    return (
        <PreloadContext.Provider
            value={{
                setPreloadRoute,
                setPreloadElement,
                isProviderActive: true,
                isPreloading: false
            }}
        >
            {children}
            {map(state, (value, key) => (
                <Preload
                    key={key}
                    element={value.element}
                    preloadKey={key}
                    removeKey={removeKey}
                />
            ))}
        </PreloadContext.Provider>
    );
};

const Preload = ({
    element,
    preloadKey,
    removeKey
}: PreloadState & { preloadKey: string; removeKey: (key: string) => void }) => {
    const clearTimerRef = useRef<number | undefined>();
    useEffect(() => {
        if (clearTimerRef.current !== undefined) {
            window.clearTimeout(clearTimerRef.current);
        }
        clearTimerRef.current = window.setTimeout(() => {
            // clearing preload after timer
            removeKey(preloadKey);
            clearTimerRef.current = undefined;
        }, CLEAR_TIMER);
    }, [preloadKey, removeKey]);

    const { isAuthenticated } = useAuth();
    if (!isAuthenticated) {
        return undefined;
    }

    return (
        <div style={{ display: "none", contentVisibility: "hidden" }}>
            <PreloadContext.Provider
                value={{
                    setPreloadRoute: () => undefined,
                    setPreloadElement: () => undefined,
                    isProviderActive: true,
                    isPreloading: true
                }}
            >
                <ErrorBoundary>
                    <Suspense>{element}</Suspense>
                </ErrorBoundary>
            </PreloadContext.Provider>
        </div>
    );
};
